import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { AuthApiError, loginWithApi } from '../services/authApi';
import { User } from '../types/auth';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  twoFactorPending: boolean;
  mfaChallengeToken: string | null;
  pendingCredentials: LoginCredentials | null;
  error: string | null;
}

interface LoginCredentials {
  email: string;
  password: string;
}

interface TwoFactorPayload {
  userId: string;
  code: string;
}

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  verifyTwoFactor: (payload: TwoFactorPayload) => Promise<boolean>;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    accessToken: null,
    isAuthenticated: false,
    isLoading: false,
    twoFactorPending: false,
    mfaChallengeToken: null,
    pendingCredentials: null,
    error: null,
  });

  const login = useCallback(
    async (credentials: LoginCredentials): Promise<void> => {
      setState((prev) => ({
        ...prev,
        isLoading: true,
        error: null,
      }));

      try {
        const apiLogin = await loginWithApi(
          credentials.email,
          credentials.password
        );

        if (apiLogin.mfaRequired) {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            user: apiLogin.user,
            accessToken: null,
            isAuthenticated: false,
            twoFactorPending: true,
            mfaChallengeToken: apiLogin.mfaChallengeToken,
            pendingCredentials: credentials,
            error: null,
          }));
          return;
        }

        setState((prev) => ({
          ...prev,
          isLoading: false,
          isAuthenticated: true,
          accessToken: apiLogin.accessToken,
          user: apiLogin.user,
          twoFactorPending: false,
          mfaChallengeToken: null,
          pendingCredentials: null,
          error: null,
        }));

        return;
      } catch (apiError: unknown) {
        if (apiError instanceof AuthApiError) {
          const apiMessage = apiError.message;
          setState((prev) => ({ ...prev, isLoading: false, error: apiMessage }));
          return;
        } else {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            error: 'Erro ao autenticar.',
          }));
          return;
        }
      }
    },
    []
  );

  const verifyTwoFactor = useCallback(
    async (payload: TwoFactorPayload): Promise<boolean> => {
      setState((prev) => ({
        ...prev,
        isLoading: true,
        error: null,
      }));

      if (state.mfaChallengeToken && state.pendingCredentials) {
        try {
          const apiLogin = await loginWithApi(
            state.pendingCredentials.email,
            state.pendingCredentials.password,
            { challengeToken: state.mfaChallengeToken, code: payload.code }
          );

          if (!apiLogin.mfaRequired) {
            setState((prev) => ({
              ...prev,
              isLoading: false,
              isAuthenticated: true,
              accessToken: apiLogin.accessToken,
              user: apiLogin.user,
              twoFactorPending: false,
              mfaChallengeToken: null,
              pendingCredentials: null,
              error: null,
            }));
            return true;
          }
        } catch (apiError: unknown) {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            error: apiError instanceof AuthApiError ? apiError.message : 'Código de verificação inválido.',
          }));
          return false;
        }
      }

      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: 'Desafio de autenticação expirado. Faça login novamente.',
      }));

      return false;
    },
    [state.mfaChallengeToken, state.pendingCredentials]
  );

  const logout = useCallback((): void => {
    setState({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: false,
      twoFactorPending: false,
      mfaChallengeToken: null,
      pendingCredentials: null,
      error: null,
    });
  }, []);

  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (state.user?.role === 'admin') {
        return true;
      }

      return state.user?.permissions?.includes(permission) ?? false;
    },
    [state.user]
  );

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        verifyTwoFactor,
        logout,
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }

  return context;
};
